import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Send, Sparkles, MapPin, Briefcase, Building2 } from 'lucide-react';
import { expressInterest, generateCoverLetter, getApiErrorMessage } from '../services/api';
import { designSystem } from '../theme';

export default function QuickApply({ open, onClose, job }) {
  const [introMessage, setIntroMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerateLetter = async () => {
    if (!job?.uuid) return;
    setGenerating(true);
    setError(null);
    try {
      const data = await generateCoverLetter(job.uuid, 'professional');
      setIntroMessage(data.cover_letter);
    } catch (err) {
      setError('Failed to generate cover letter. Try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await expressInterest(job.uuid, introMessage || null);
      setSuccess(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to express interest. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIntroMessage('');
    setSuccess(false);
    setError(null);
    onClose();
  };

  if (!job) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight="600">
          {success ? 'Interest Submitted!' : 'Express Interest'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'success.50', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Send size={28} color="#0D7C5F" />
            </Box>
            <Typography variant="body1" gutterBottom>
              Your interest in <strong>{job.title}</strong> at <strong>{job.company}</strong> has been sent.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The hiring manager will be notified and can reach out to you directly.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Job Summary */}
            <Card variant="outlined" sx={{ borderRadius: 2, mb: 2, bgcolor: 'background.default' }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="600">{job.title}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {job.company && (
                    <Chip icon={<Building2 size={14} />} label={job.company} size="small" variant="outlined" />
                  )}
                  {job.location && (
                    <Chip icon={<MapPin size={14} />} label={job.location} size="small" variant="outlined" />
                  )}
                  {job.job_type && (
                    <Chip icon={<Briefcase size={14} />} label={job.job_type.replace('_', ' ')} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            </Card>

            <Divider sx={{ my: 2 }} />

            {/* Intro Message */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="600">
                Introduction Message (optional)
              </Typography>
              <Button
                size="small"
                startIcon={generating ? <CircularProgress size={14} /> : <Sparkles size={14} />}
                onClick={handleGenerateLetter}
                disabled={generating}
                sx={{ textTransform: 'none' }}
              >
                {generating ? 'Generating...' : 'AI Cover Letter'}
              </Button>
            </Box>
            <TextField
              value={introMessage}
              onChange={(e) => setIntroMessage(e.target.value)}
              fullWidth
              multiline
              rows={6}
              placeholder="Tell the employer why you're a great fit for this role..."
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
            sx={designSystem.button.contained}
          >
            {submitting ? 'Sending...' : 'Send Interest'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
